/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { CreateTextureInterface } from '../../canvas-common/interfaces/create-texture.interface';
import { TicketContainer } from './TicketContainer';
import { RenderDependencies } from '@appPlanApplication/RenderDependencies';
import { Camera } from '@appCanvasCommon/core/Camera';
import { SpriteConstraint } from './SpriteConstraint';
import { SpriteMilestone } from './SpriteMilestone';
import { SpriteTask } from './SpriteTask';
import { SpriteTicket } from './SpriteTicket';
import { TaskRenderer } from '../ticket-renderer/';
import { LayerManager } from '@appCanvasCommon/layers/LayerManager';
import { Texture, utils, Assets } from 'pixi.js-legacy';
import { Subject } from 'rxjs';
import { TicketCollision } from '../ticket-collision/';
import { FAILED_TO_LOAD_TICKET_TEXTURES } from '@touchplan/strings';
import { TICKET_TEXTURE_OPTIONS } from './TICKET_TEXTURE_OPTIONS';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { fbReporting } from 'src/app/plain-javascript/background-scripts/fb-reporting';
import { TicketType } from '@touchplan/common';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';
import { IAssetManifest } from '@appCanvasCommon/loader/loader.interface';
import { takeUntil } from 'rxjs/operators';
import { IPrintConfiguration } from '../../../app-data/constants/plan-print.model';

const planApplicationBundleName: string = 'plan-application';
const manifest: IAssetManifest = {
	bundles: [
		{
			name: planApplicationBundleName,
			assets: [
				{
					alias: TICKET_TEXTURE_OPTIONS.TEXTURE_TICKET_ATLAS_REFERENCE,
					src: TICKET_TEXTURE_OPTIONS.TEXTURE_ATLAS_PATH,
				},
				{
					alias: TICKET_TEXTURE_OPTIONS.TEXTURE_TICKET_OVERLAP,
					src: TICKET_TEXTURE_OPTIONS.TEXTURE_TICKET_OVERLAP,
				},
				{
					alias: TICKET_TEXTURE_OPTIONS.TEXTURE_PIN_DASH_TEXTURE,
					src: TICKET_TEXTURE_OPTIONS.TEXTURE_PIN_DASH_TEXTURE,
				},
			],
		},
	],
};

type ManagerFunctionKeys = keyof FunctionMember<TicketManager>;

/**
 * This class has a dependency to the TicketMapper. Where the TicketMapper is responsible for linking data to a DataContainer,
 *  the TicketManager is responsible for updating the SpriteTicket with the data update.
 */
export class TicketManager {
	public update$: Subject<void> = new Subject();
	public ticketCollision: TicketCollision;
	/**
	 * A cached object mapping a ticket $id to a SpriteTicket. This is used to prevent traversal of the scene graph
	 * @memberOf TicketManager
	 */
	private _cache: Map<string, SpriteMilestone | SpriteConstraint | SpriteTask>;
	/**
	 * Instance of a TaskRenderer
	 */
	public taskRenderer: TaskRenderer;
	/**
	 * Instance of LayerManager
	 */
	public layerManager: LayerManager;
	public canvasAngularLand: CanvasAngularLand;
	public camera: Camera;

	private _listener: utils.EventEmitter;
	private _listenerEvents: ManagerFunctionKeys[];
	private _render: () => any;
	private _logger: ILoggerService;
	private _shutdownSubject$: Subject<void> = new Subject<void>();
	/**
	 * @param {CanvasAngularLand} canvasAngularLand
	 * @param {LayerManager} layerManager
	 * @param {Camera} camera The camera
	 * @param renderFn The renderer update function
	 * @param {CreateTextureInterface} [rendererOptions]
	 */
	constructor(
		canvasAngularLand: CanvasAngularLand,
		private userPreferenceStateService: UserPreferenceStateService,
		layerManager: LayerManager,
		camera: Camera,
		renderFn: () => any,
		rendererOptions?: CreateTextureInterface
	) {
		this._logger = LoggerServiceFactory.create('TicketManager');
		this._cache = new Map();
		this.taskRenderer = new TaskRenderer(null, rendererOptions);

		// Add Ticket texture as a static variable to PIXI.Texture. Adding it there so it is easy to access later...
		(<any>Texture).TASK_TEXTURE = this.taskRenderer.getTexture('ticket');
		(<any>Texture).TASK_OUTLINE_SELECTED = this.taskRenderer.getTexture('ticketOutlineSelectedMesh');
		(<any>Texture).TASK_OUTLINE_ERROR = this.taskRenderer.getTexture('ticketOutlineErrorMesh');

		this.layerManager = layerManager;
		this.canvasAngularLand = canvasAngularLand;
		this.camera = camera;
		this._render = renderFn;

		this.ticketCollision = new TicketCollision(
			canvasAngularLand,
			this.userPreferenceStateService,
			this,
			this.camera,
			this._render
		);

		RenderDependencies.addDependency('ticketTextureAtlas');
		RenderDependencies.addDependency('ticketOverlap');
		this._loadDependencies();
	}

	/**
	 * Force interaction for every ticket to be disabled
	 * @private
	 */
	public disableAllTicketInteraction(): void {
		this._cache.forEach((t) => t.ticketContainer.SURFACE.disableInteraction());
	}

	/**
	 * Force every interaction for every ticket to be enabled
	 * @private
	 */
	public enableAllTicketInteraction(): void {
		this._cache.forEach((t) => t.ticketContainer.SURFACE.enableInteraction());
	}

	/**
	 * Loads the ticket textures.
	 * @param {string} referenceId
	 * @param {string} path
	 */
	private async _loadDependencies(): Promise<void> {
		try {
			if (!Assets.cache.has(TICKET_TEXTURE_OPTIONS.TEXTURE_TICKET_ATLAS_REFERENCE)) {
				await Assets.init({ manifest });
			}
			const loadedBundle = await Assets.loadBundle(planApplicationBundleName);
			if (!loadedBundle) {
				return this._onTextureAtlasLoadedError(
					new Error(`Failed to load bundle ${planApplicationBundleName}.`)
				);
			}

			this._onTextureAtlasLoaded();
		} catch (e) {
			this._onTextureAtlasLoadedError(e as Error);
		}
	}
	/**
	 * Called once the ticket textures have been loaded.
	 *
	 * Once ticket texture atlas is loaded, it iterates through the PIXI.Texture and adds it to the TicketManager.resources list for easy access later.
	 * @param {PIXI.loaders.Loader} loader
	 * @param {PIXI.loaders.ResourceDictionary} resources
	 */
	private _onTextureAtlasLoaded(): void {
		RenderDependencies.markAsLoaded('ticketTextureAtlas');
		RenderDependencies.markAsLoaded('ticketOverlap');
	}
	/**
	 * Invoked if an error occurs.
	 * @param err
	 */
	private _onTextureAtlasLoadedError(err: Error): void {
		this._logger.log('ERROR', err);
		fbReporting.reportError(new Error('Failed to load ticket textures'), err);
		window.Logging.error(FAILED_TO_LOAD_TICKET_TEXTURES);
	}
	/**
	 * If no ticket exists, then this is a new ticket with new data. Create the view
	 * @param {TicketContainer} ticketContainer The DataContainer associated with this ticket
	 */
	private createTicket(ticketContainer: TicketContainer): SpriteTask {
		const t = new SpriteTask(this.taskRenderer.getTexture('ticket'), this.canvasAngularLand, ticketContainer);

		// Add our ticket to the "SURFACE" sorting layer.
		ticketContainer.SURFACE.addChild(t);

		// Caches our SpriteTicket. This makes it easier to retrieve later.
		this._cache.set(ticketContainer.getData().$id, t);
		t.updateData(this.canvasAngularLand, ticketContainer);
		if (RenderDependencies.firstDraw) {
			t.update(this.canvasAngularLand, ticketContainer);
		}

		return t;
	}
	/**
	 * Called when a new milestone has been added.
	 * @param {TicketContainer} ticketContainer
	 */
	private createMilestone(ticketContainer: TicketContainer): SpriteMilestone {
		const m = new SpriteMilestone(
			utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_MILESTONE_NAME],
			this.canvasAngularLand,
			ticketContainer
		);

		// Add our ticket to the "SURFACE" sorting layer.
		ticketContainer.SURFACE.addChild(m);

		// Caches our SpriteTicket. This makes it easier to retrieve later.
		this._cache.set(ticketContainer.getData().$id, m);
		m.updateData(this.canvasAngularLand, ticketContainer);
		if (RenderDependencies.firstDraw) {
			m.update(this.canvasAngularLand, ticketContainer);
		}
		return m;
	}
	/**
	 * Called when a new constraint has been added.
	 * @param {TicketContainer} ticketContainer
	 */
	private createConstraint(ticketContainer: TicketContainer): SpriteConstraint {
		const c = new SpriteConstraint(
			utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_NAME],
			utils.TextureCache[TICKET_TEXTURE_OPTIONS.TEXTURE_CONSTRAINT_NAME],
			this.canvasAngularLand,
			ticketContainer
		);

		ticketContainer.SURFACE.addChild(c);

		// Caches our SpriteTicket. This makes it easier to retrieve later.
		this._cache.set(ticketContainer.getData().$id, c);
		c.updateData(this.canvasAngularLand, ticketContainer);
		if (RenderDependencies.firstDraw) {
			c.update(this.canvasAngularLand, ticketContainer);
		}
		return c;
	}

	public update = (
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		forceDirty: boolean
	): void => {
		const spriteTicket: SpriteTicket = this._cache.get(ticketContainer.getData().$id) as SpriteTicket;
		if (!spriteTicket) {
			return;
		}
		spriteTicket.update(canvasAngularLand, ticketContainer, forceDirty);
	};

	/**
	 * Called everytime a TicketMapper updates data on a data container
	 * @param {PlanState} planState the current plan state
	 * @param {TicketContainer} ticketContainer The DataContainer that was updated
	 * @returns {any}
	 */
	public updateData = (canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) => {
		const t: SpriteTicket = this._cache.get(ticketContainer.getData().$id);
		if (!t) {
			const data = ticketContainer.getData();
			const lm = ticketContainer.ticketManager.layerManager;
			// Ticket does not exist yet, so create the view
			if (!data.view.type || data.view.type === TicketType.Task) {
				const t = this.createTicket(ticketContainer);
				if (data.isGhost) {
					//TODO: Need to fix da Ghost
					// let ticket = dataContainer.components.get('ticket');
					// ticket.active = false;
					// ticket.visible = true;
					// ticket.alpha = 0.3;
				}
				return t;
			} else if (data.view.type === 'milestone') {
				const t = this.createMilestone(ticketContainer);
				if (data.isGhost) {
					//ticketContainer.components.get('ticket').active = false;
					//ticketContainer.components.get('ticket').visible = false;
				}
				return t;
			} else {
				const t = this.createConstraint(ticketContainer);
				if (data.isGhost) {
					//ticketContainer.components.get('ticket').active = false;
					//ticketContainer.components.get('ticket').visible = false;
				}
				return t;
			}
		}

		// Update ticket data.
		t.updateData(this.canvasAngularLand, ticketContainer);
		// let data = dataContainer.getData();
		// dataContainer.components.active = dataContainer.visible;
		//dataContainer.updateData(this.planState, dataContainer);
	};
	/**
	 * When the renderer is destroyed, this method is called.
	 */
	public destroy = () => {
		this._cache.clear();
		this.ticketCollision.destroy();

		this._shutdownSubject$.next();
		this._shutdownSubject$.complete();

		this._cache = null;
		this.taskRenderer.destroy();
		this.taskRenderer = null;
		this.canvasAngularLand = null;

		// Clean-up the listener
		this._listenerEvents.forEach((eventName) => {
			this._listener.off(eventName, this[eventName]);
		});
	};
	/**
	 * Called when a ticket is destroyed.
	 * @param {CanvasAngularLand} canvasAngularLand
	 * @param {TicketContainer} dataContainer
	 */
	public destroyTicket = (canvasAngularLand: CanvasAngularLand, dataContainer: TicketContainer) => {
		const key = dataContainer.getData().$id;
		if (this._cache.get(key)) {
			this._cache.delete(key);
		}
		return this;
	};
	/**
	 * Given the {ticketId}, return the corresponding SpriteTask, SpriteConstraint or SpriteMilestone
	 * @param {string} ticketId
	 */
	public get(ticketId: string): SpriteTask | SpriteConstraint | SpriteMilestone {
		return this._cache.get(ticketId);
	}
	/**
	 * Registers an event listener
	 * @param eventEmitter The emitter to listen to
	 * @param events A list of events to listen to.
	 */
	public listen(eventEmitter: utils.EventEmitter, ...events: ManagerFunctionKeys[]) {
		this._listener = eventEmitter;
		this._listenerEvents = events;
		events.forEach((eventName) => {
			eventEmitter.on(eventName, this[eventName]);
		});
	}
}
